import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../cn';
import type { ColorName, ColorVariant, Style } from './buttonClasses';
import { composeVariant, inputVariantClasses } from './buttonClasses';
import type { BaseProps, Size } from './types';
import {
  shapeRounded,
  sizeFont,
  sizeHeight,
  sizeIconClass,
  sizeInnerGap,
  sizeIconPaddingL,
  sizeIconPaddingR,
  sizePaddingL,
} from './types';

export type SelectOption = {
  value: string;
  label: string;
  icon?: ReactNode;
  /**
   * Nesting in a two-level list: 0 = a main entry (bold, starts a section with a hairline above
   * it), 1 = an entry under the main one before it (indented past its icon, quieter). Omit for a flat list.
   */
  level?: 0 | 1;
  description?: string;
  /** Per-item highlight color for FilterSelect dropdown. */
  color?: ColorName;
  /** Per-item highlight tone for FilterSelect dropdown. Defaults to `'filled'` when `color` is set. */
  tone?: Style;
  /** Optional action button rendered on the right side of the option (FilterSelect only). */
  action?: {
    icon: ReactNode;
    onClick: (value: string, e: React.MouseEvent) => void;
    className?: string;
    /** What the button does, for screen readers and as its hover title — it is icon-only. */
    label?: string;
    /** Close the dropdown after the click — for an action that opens something over the page (a modal). */
    closes?: boolean;
  };
  /**
   * Opens a deeper list instead of closing (FilterSelect only): selecting it still calls `onChange`,
   * but the popover stays open with its filter cleared, so the caller can swap `options` for the
   * next level — a "back" row is a drill row too. After a drill the cursor lands on the first
   * non-drill row of the new list.
   */
  drill?: boolean;
};

export type SelectProps = Omit<ComponentProps<'select'>, 'size'> &
  BaseProps & {
    size?: Size;
    /** Color axis — `'blue' | 'red' | 'green' | …`. Defaults to `'gray'`. */
    color?: ColorName;
    /** Style axis — `'filled' | 'outline' | 'soft' | 'underline'`. Defaults to `'filled'`. */
    tone?: Style;
    options: SelectOption[];
    placeholder?: string;
  };

export default function Select({
  size = 'md',
  bordered = true,
  shape,
  icon,
  rIcon,
  error,
  color,
  tone,
  className,
  disabled,
  readOnly,
  options,
  placeholder,
  isUpdating: _isUpdating,
  isSuccess: _isSuccess,
  ...props
}: SelectProps & { isUpdating?: boolean; isSuccess?: boolean; readOnly?: boolean }) {
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  return (
    <div
      className={cn(
        'inline-flex items-center font-sans transition-colors relative',
        sizeHeight(size),
        shapeRounded(shape, size),
        sizeFont(size),
        sizeInnerGap(size),
        'border',
        inputVariantClasses(effectiveVariant, bordered, !!error, !!disabled, !!readOnly),
        className,
      )}>
      {/* Select covers full container for click handling */}
      <select
        disabled={disabled || readOnly}
        className={cn(
          'absolute inset-0 w-full h-full bg-transparent outline-hidden border-none appearance-none opacity-0',
          'cursor-pointer',
          disabled && 'cursor-not-allowed',
        )}
        {...props}
        defaultValue={props.defaultValue ?? (placeholder ? '' : undefined)}>
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {/* Visual display layer */}
      {icon && (
        <span className={cn('shrink-0 flex items-center justify-center pointer-events-none', sizeIconPaddingL(size))}>
          <span className={cn(sizeIconClass(size), '*:size-full')}>{icon}</span>
        </span>
      )}
      <span className={cn('flex-1 min-w-0 truncate pointer-events-none', !icon && sizePaddingL(size))}>
        {options.find((opt) => opt.value === props.value)?.label ??
          options.find((opt) => opt.value === props.defaultValue)?.label ??
          placeholder}
      </span>
      {/* Chevron or custom right icon */}
      <span className={cn('shrink-0 flex items-center justify-center pointer-events-none', sizeIconPaddingR(size))}>
        <span className={cn(sizeIconClass(size), '*:size-full')}>
          {rIcon ?? (
            <svg viewBox="0 0 20 20" fill="currentColor" className="size-full">
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </span>
      </span>
    </div>
  );
}
